import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { useFlexibleList } from '../../../../hooks/useSWR';

const DeviationTimelineTab = ({ 
  selectedRecord
}) => {
  const { theme } = useTheme();

  // Fetch Deviation timeline entries for the selected record
  const { data: timelineEntries = [], isLoading, error } = useFlexibleList(
    selectedRecord?.id ? `/deviation-timeline/by-deviation/${selectedRecord.id}` : null
  );

  if (!selectedRecord) {
    return (
      <div className={cn("p-4 text-sm", theme.text.secondary)}>
        No Deviation selected
      </div>
    );
  }

  // Function to get action icon based on action type
  const getActionIcon = (action) => {
    const iconClass = "w-4 h-4";
    switch (action?.toUpperCase()) {
      case 'OPENED':
      case 'OPEN':
        return (
          <svg className={cn(iconClass, "text-green-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        );
      case 'CLOSED':
      case 'CLOSE':
        return (
          <svg className={cn(iconClass, "text-red-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'CREATED':
      case 'DRAFT':
        return (
          <svg className={cn(iconClass, "text-green-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        );
      case 'STATUS_CHANGED':
        return (
          <svg className={cn(iconClass, "text-blue-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
        );
      case 'ASSIGNED':
      case 'ASSIGN':
        return (
          <svg className={cn(iconClass, "text-purple-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      case 'INVESTIGATE':
      case 'INVESTIGATION':
        return (
          <svg className={cn(iconClass, "text-yellow-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        );
      case 'CORRECT':
      case 'CORRECTIVE':
        return (
          <svg className={cn(iconClass, "text-orange-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        );
      case 'VERIFY':
      case 'VERIFICATION':
        return (
          <svg className={cn(iconClass, "text-indigo-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className={cn(iconClass, "text-gray-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (isLoading) {
    return (
      <div className={cn("p-4 text-center", theme.text.muted)}>
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-sm">Loading timeline...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("p-4 text-center text-sm", theme.text.error)}>
        Error loading timeline: {error.message || 'Unknown error'}
      </div>
    );
  }

  if (timelineEntries.length === 0) {
    return (
      <div className={cn("p-4 text-center text-sm", theme.text.muted)}>
        No timeline entries found
      </div>
    );
  }

  return (
    <div 
      className="p-4 flex-1 overflow-y-auto advanced-table-scroll"
      style={{ 
        scrollbarWidth: 'thin',
        minHeight: 0,
        maxHeight: '100%'
      }}
    >
      <div className="relative">
        {/* Timeline line */}
        <div className={cn("absolute left-4 top-0 bottom-0 w-0.5", theme.colors.border)}></div>
        
        <div className="space-y-4">
          {timelineEntries.map((entry, index) => (
            <div key={entry.id || index} className="relative flex gap-4">
              {/* Icon */}
              <div className={cn(
                "relative z-10 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
                theme.colors.background,
                theme.border.light,
                "border-2"
              )}>
                {getActionIcon(entry.action)}
              </div>
              
              {/* Content */}
              <div className="flex-1 pb-4">
                <div className={cn("text-sm font-medium mb-1", theme.text.primary)}>
                  {entry.action || 'Action'}
                </div>
                {entry.comment && (
                  <div className={cn("text-sm mb-2", theme.text.secondary)}>
                    {entry.comment}
                  </div>
                )}
                <div className="flex items-center gap-4 text-xs">
                  <div className={cn("flex items-center gap-1", theme.text.muted)}>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>{entry.user?.userDisplayName || entry.user?.name || 'Unknown'}</span>
                  </div>
                  <div className={cn("flex items-center gap-1", theme.text.muted)}>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{formatDateTime(entry.performedAt)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DeviationTimelineTab;

